import { getLivabilityAuthHeaders, livFetchResponse, normalizeNoneValues } from './client'
import type {
  ChatSearchRequest,
  ChatSearchResponse,
  ChatSearchStreamCallbacks,
  ListingType
} from './types'

export async function deleteChatSession(sessionId: string): Promise<void> {
  const response = await livFetchResponse(`/search/sessions/${sessionId}`, {
    method: 'DELETE'
  })
  if (!response.ok) {
    throw new Error(`Livability API error: ${response.status} ${response.statusText}`)
  }
}

export async function chatSearchStream(
  query: string,
  sessionId: string | null | undefined,
  callbacks: ChatSearchStreamCallbacks,
  offset?: number,
  listingType: ListingType = 'rent'
): Promise<void> {
  const payload: ChatSearchRequest = {
    query,
    session_id: sessionId || null,
    offset: offset ?? 0,
    listing_type: listingType
  }

  const response = await livFetchResponse('/search/chat-v2/stream', {
    method: 'POST',
    headers: getLivabilityAuthHeaders({
      headers: { 'Content-Type': 'application/json' }
    }),
    body: JSON.stringify(payload)
  })

  if (!response.ok) {
    if (response.status === 503) {
      callbacks.onError(
        new Error(
          'AI search service is not available. Please try again later.'
        )
      )
      return
    }
    callbacks.onError(new Error('Failed to perform chat search'))
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError(new Error('Streaming not supported'))
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const events = buffer.split('\n\n')
    buffer = events.pop() || ''

    for (const eventBlock of events) {
      const lines = eventBlock.split('\n')
      let eventType = ''
      let dataLine = ''

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          eventType = line.slice(7)
        } else if (line.startsWith('data: ')) {
          dataLine = line.slice(6)
        }
      }

      if (!dataLine) continue

      if (eventType === 'token') {
        const parsed = JSON.parse(dataLine)
        callbacks.onToken(parsed.text || '')
      } else if (eventType === 'done') {
        const parsed = normalizeNoneValues<ChatSearchResponse>(
          JSON.parse(dataLine)
        )
        callbacks.onDone(parsed)
      } else if (eventType === 'error') {
        const parsed = JSON.parse(dataLine)
        callbacks.onError(new Error(parsed.error || 'Stream error'))
      }
    }
  }
}
